import { EntityStatusChip } from '../components/entity-detail/EntityStatusChip.js';
import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../i18n/index.js';
import { useMemo, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Card,
  Tabs,
  Modal,
  Button,
  Spin,
  Typography,
  Descriptions,
  Divider,
  Switch,
  Tooltip,
} from 'antd';
import { ApiError } from '../shared/api-client/index.js';

import { ActiveSessionsPanel } from '../features/settings/components/ActiveSessionsPanel.js';
import { AiAgentSettings } from '../features/settings/components/AiAgentSettings.js';
import { CalendarIntegrationsPanel } from '../features/settings/components/CalendarIntegrationsPanel.js';
import { TelegramOutboundPanel } from '../features/settings/components/TelegramOutboundPanel.js';
import { WorkspaceDataPanel } from '../features/settings/components/WorkspaceDataPanel.js';
import { ConversionDigestPanel } from '../features/settings/components/ConversionDigestPanel.js';
import { NavigationSettingsPanel } from '../features/navigation/components/NavigationSettingsPanel.js';
import { sendVerificationEmail } from '../features/account/api/account.client.js';
import { TeamPanel } from '../features/team/components/TeamPanel.js';
import { PageHeader } from '../shared/ui/PageHeader.js';
import {
  useUpdateWorkspaceSettingsMutation,
  useWorkspaceSettings,
} from '../features/settings/hooks/useSettings.js';
import {
  useCurrentScope,
  useCurrentUser,
  useCurrentWorkspaceSettings,
} from '../shared/scope/useCurrentScope.js';

const { Title, Text } = Typography;

const ADR_FLAGS = [
  { id: 'chat-simple-plan-v1', name: 'simple', parents: [] },
  { id: 'chat-planner-first-v1', name: 'planner', parents: ['chat-simple-plan-v1'] },
  { id: 'chat-compound-plan-v1', name: 'compound', parents: ['chat-simple-plan-v1'] },
  {
    id: 'chat-planner-batches-v1',
    name: 'batches',
    parents: ['chat-simple-plan-v1', 'chat-planner-first-v1', 'chat-compound-plan-v1'],
  },
  { id: 'chat-compound-batch-approve-all-v1', name: 'approve', parents: [] },
] as const;

const SETTINGS_TAB_KEYS = [
  'profile',
  'workspace',
  'ai',
  'flags',
  'team',
  'security',
  'data',
  'navigation',
  'telegram',
  'integrations',
] as const;

export interface SettingsPageProps {
  /** Стартовая вкладка. Без пропа — «Профиль». */
  initialTab?: string;
}

export default function SettingsPage({ initialTab = 'profile' }: SettingsPageProps): ReactElement {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const scope = useCurrentScope();
  const workspaceSettings = useCurrentWorkspaceSettings();
  // Вкладка — в URL и в обе стороны: возврат из OAuth приходит на
  // /settings?tab=integrations (параметр уважается, только если вкладка
  // существует), а переключение вкладки переписывает параметр, чтобы ссылку
  // на нужный раздел можно было передать.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab !== null && (SETTINGS_TAB_KEYS as readonly string[]).includes(requestedTab)
      ? requestedTab
      : initialTab;
  const setActiveTab = (next: string): void => {
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        // Возврат на стартовую вкладку маршрута — канонический URL без параметра.
        if (next === initialTab) params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  };
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [flagError, setFlagError] = useState<'forbidden' | 'failed' | null>(null);

  async function handleSendVerification(): Promise<void> {
    setSendingVerification(true);
    try {
      await sendVerificationEmail();
      setVerificationSent(true);
    } finally {
      setSendingVerification(false);
    }
  }
  const settingsQuery = useWorkspaceSettings();
  const updateSettingsMutation = useUpdateWorkspaceSettingsMutation();

  const proModeEnabled = settingsQuery.data?.featureFlags['pro-mode'] ?? false;
  const canManageAdr = scope?.role === 'owner' || scope?.role === 'admin';
  const flags = settingsQuery.data?.featureFlags ?? {};
  const adrFullOn = ADR_FLAGS.slice(0, 4).every((flag) => flags[flag.id] === true);
  const adrPartial = !adrFullOn && ADR_FLAGS.some((flag) => flags[flag.id] === true);

  async function updateAdrFlags(featureFlags: Record<string, boolean>): Promise<void> {
    if (!canManageAdr) return;
    setFlagError(null);
    try {
      await updateSettingsMutation.mutateAsync({ featureFlags });
    } catch (error) {
      setFlagError(error instanceof ApiError && error.status === 403 ? 'forbidden' : 'failed');
    }
  }

  function toggleAdrFlag(id: string, checked: boolean): void {
    const changes: Record<string, boolean> = { [id]: checked };
    if (!checked) {
      for (const flag of ADR_FLAGS) {
        if (flag.parents.some((parent) => changes[parent] === false)) changes[flag.id] = false;
      }
    }
    void updateAdrFlags(changes);
  }
  const tabs = useMemo(
    () => [
      { key: 'profile', label: t('crm.settings.tabs.profile') },
      { key: 'workspace', label: t('crm.settings.tabs.workspace') },
      { key: 'ai', label: t('crm.settings.tabs.ai') },
      {
        key: 'flags',
        label: <span data-testid="settings-tab-flags">{t('crm.settings.tabs.flags')}</span>,
      },
      { key: 'team', label: t('crm.settings.tabs.team') },
      { key: 'security', label: t('crm.settings.tabs.security') },
      {
        key: 'data',
        label: <span data-testid="settings-tab-data">{t('crm.settings.tabs.data')}</span>,
      },
      {
        key: 'navigation',
        label: (
          <span data-testid="settings-tab-navigation">{t('crm.settings.tabs.navigation')}</span>
        ),
      },
      {
        key: 'telegram',
        label: <span data-testid="settings-tab-telegram">{t('crm.settings.tabs.telegram')}</span>,
      },
      {
        key: 'integrations',
        label: (
          <span data-testid="settings-tab-integrations">{t('crm.settings.tabs.integrations')}</span>
        ),
      },
    ],
    [t],
  );

  async function handleConfirmProMode(): Promise<void> {
    await updateSettingsMutation.mutateAsync({ featureFlags: { 'pro-mode': true } });
    setIsConfirmOpen(false);
  }

  async function handleToggleProMode(): Promise<void> {
    if (proModeEnabled) {
      await updateSettingsMutation.mutateAsync({ featureFlags: { 'pro-mode': false } });
      return;
    }
    setIsConfirmOpen(true);
  }

  function renderAdrFlag(flag: (typeof ADR_FLAGS)[number]): ReactElement {
    const parents: readonly string[] =
      flag.name === 'approve' ? ['chat-compound-plan-v1'] : flag.parents;
    const missing = parents.filter((parent) => flags[parent] !== true);
    const blockedByParents = missing.length > 0 && flags[flag.id] !== true;
    const missingNames = missing.map((id) => {
      const parent = ADR_FLAGS.find((item) => item.id === id);
      return parent ? t(`crm.settings.adr_${parent.name}`) : id;
    });
    const hint = !canManageAdr
      ? t('crm.settings.adr_owner_only')
      : blockedByParents
        ? `${t(flag.name === 'approve' ? 'crm.settings.adr_requires_plural' : 'crm.settings.adr_requires')} ${missingNames.join(', ')}`
        : '';
    return (
      <div
        key={flag.id}
        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <Text strong style={{ display: 'block' }}>
            {t(`crm.settings.adr_${flag.name}`)}
          </Text>
          <Text type="secondary">{t(`crm.settings.adr_${flag.name}_description`)}</Text>
        </div>
        <Tooltip title={hint}>
          <span tabIndex={hint ? 0 : undefined}>
            <Switch
              aria-label={t(`crm.settings.adr_${flag.name}`)}
              data-testid={`flag-${flag.id}`}
              checked={flags[flag.id] === true}
              disabled={
                !canManageAdr ||
                blockedByParents ||
                updateSettingsMutation.isPending ||
                settingsQuery.isLoading ||
                settingsQuery.isError
              }
              onChange={(checked) => toggleAdrFlag(flag.id, checked)}
            />
          </span>
        </Tooltip>
      </div>
    );
  }

  const renderTabContent = () => {
    if (activeTab === 'profile') {
      return (
        <Card>
          <Descriptions title={t('crm.settings.tabs.profile')} column={1}>
            <Descriptions.Item label={t('crm.settings.field.email')}>
              {user?.email ?? '—'}
              {user && !user.emailVerified ? (
                <EntityStatusChip tone="warning" className="ml-2">
                  {i18n.t('crm.features.pages.settingspage.не_подтвержден')}
                </EntityStatusChip>
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label={i18n.t('crm.features.pages.settingspage.имя')}>
              {user?.name ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={i18n.t('crm.features.pages.settingspage.роль')}>
              {scope?.role ?? '—'}
            </Descriptions.Item>
          </Descriptions>

          {user && !user.emailVerified ? (
            <Alert
              type="info"
              showIcon
              className="mt-4"
              title={i18n.t('crm.features.pages.settingspage.адрес_не_подтвержден')}
              description={
                <div className="flex flex-col gap-2">
                  <span>
                    {i18n.t(
                      'crm.features.pages.settingspage.подтверждение_нужно_чтобы_восстановить_пар',
                    )}
                  </span>
                  {verificationSent ? (
                    <Text type="success">
                      {i18n.t('crm.features.pages.settingspage.письмо_отправлено_проверьте_почту')}
                    </Text>
                  ) : (
                    <Button
                      size="small"
                      loading={sendingVerification}
                      onClick={() => void handleSendVerification()}
                    >
                      {i18n.t('crm.features.pages.settingspage.отправить_письмо')}
                    </Button>
                  )}
                </div>
              }
            />
          ) : null}
        </Card>
      );
    }
    if (activeTab === 'workspace') {
      return (
        <Card>
          <Descriptions title={t('crm.settings.tabs.workspace')} column={1}>
            <Descriptions.Item label={t('crm.settings.field.workspace_id')}>
              {workspaceSettings?.workspaceId ?? scope?.workspaceId ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('crm.settings.field.feature_flags')}>
              {Object.keys(settingsQuery.data?.featureFlags ?? {}).join(', ') || '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      );
    }
    if (activeTab === 'ai') {
      return <AiAgentSettings />;
    }
    if (activeTab === 'flags') {
      return (
        <Card aria-label={t('crm.settings.tabs.flags')}>
          <div className="mb-6">
            <Title
              level={5}
              style={{
                margin: 0,
                marginBottom: 4,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: 'var(--ant-color-text-secondary)',
              }}
            >
              {t('crm.settings.flags_title')}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('crm.settings.flags_description')}
            </Text>
          </div>

          <Card
            type="inner"
            style={{
              backgroundColor: 'var(--ant-color-bg-elevated)',
              borderColor: 'var(--ant-color-border)',
            }}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <Text strong style={{ display: 'block' }}>
                  {t('crm.settings.pro_mode_title')}
                </Text>
                <Text type="secondary">{t('crm.settings.pro_mode_description')}</Text>
              </div>
              <Button
                data-testid="flag-pro-mode-toggle"
                type={proModeEnabled ? 'default' : 'primary'}
                loading={updateSettingsMutation.isPending}
                disabled={settingsQuery.isLoading}
                onClick={() => void handleToggleProMode()}
              >
                {proModeEnabled
                  ? t('crm.settings.disable_pro_mode')
                  : t('crm.settings.enable_pro_mode')}
              </Button>
            </div>
          </Card>
          <Card type="inner" style={{ marginTop: 16 }} title={t('crm.settings.adr_title')}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Text strong style={{ display: 'block' }}>
                    {t('crm.settings.adr_master')}
                  </Text>
                  <Text type="secondary">{t('crm.settings.adr_master_description')}</Text>
                  {adrPartial && (
                    <Text type="secondary" style={{ display: 'block' }}>
                      {t('crm.settings.adr_custom')}
                    </Text>
                  )}
                </div>
                <Tooltip title={!canManageAdr ? t('crm.settings.adr_owner_only') : ''}>
                  <span tabIndex={!canManageAdr ? 0 : undefined}>
                    <Switch
                      aria-label={t('crm.settings.adr_master')}
                      data-testid="flag-adr-master"
                      checked={adrFullOn}
                      disabled={
                        !canManageAdr ||
                        updateSettingsMutation.isPending ||
                        settingsQuery.isLoading ||
                        settingsQuery.isError
                      }
                      onChange={(checked) =>
                        void updateAdrFlags({
                          'chat-simple-plan-v1': checked,
                          'chat-planner-first-v1': checked,
                          'chat-compound-plan-v1': checked,
                          'chat-planner-batches-v1': checked,
                          'chat-compound-batch-approve-all-v1': false,
                        })
                      }
                    />
                  </span>
                </Tooltip>
              </div>
              <div className="flex flex-col gap-4" data-testid="adr-fine-tuning">
                <Text strong>{t('crm.settings.adr_fine_tuning')}</Text>
                {ADR_FLAGS.slice(0, 4).map(renderAdrFlag)}
              </div>
              <Divider style={{ margin: 0 }} />
              <div data-testid="adr-approve-all">{ADR_FLAGS.slice(4).map(renderAdrFlag)}</div>
              {flagError && (
                <Alert type="error" showIcon title={t(`crm.settings.adr_error_${flagError}`)} />
              )}
              <Text type="secondary">{t('crm.settings.adr_hint')}</Text>
            </div>
          </Card>
        </Card>
      );
    }
    if (activeTab === 'team') {
      return (
        <Card>
          <TeamPanel />
        </Card>
      );
    }
    if (activeTab === 'security') {
      return (
        <Card>
          <ActiveSessionsPanel />
        </Card>
      );
    }
    if (activeTab === 'data') return <WorkspaceDataPanel />;
    if (activeTab === 'navigation') return <NavigationSettingsPanel />;
    if (activeTab === 'telegram')
      return (
        <div className="flex flex-col gap-6">
          <TelegramOutboundPanel />
          <ConversionDigestPanel />
        </div>
      );
    if (activeTab === 'integrations') return <CalendarIntegrationsPanel />;
    return null;
  };

  return (
    <main
      className="flex flex-col p-6 mx-auto w-full h-full min-h-screen"
      style={{
        backgroundColor: 'var(--ant-color-bg-layout)',
        maxWidth: 1200,
        gap: 24,
      }}
    >
      <PageHeader title={t('crm.settings.title')} subtitle={t('crm.settings.subtitle')} />

      <Card styles={{ body: { padding: '24px 24px 40px' } }} style={{ flex: 1 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabs}
          className="ant-tabs-warm"
        />

        <div className="mt-6 w-full max-w-3xl">
          {settingsQuery.isLoading ? (
            <div className="flex items-center gap-2 py-8">
              <Spin size="small" /> <Text type="secondary">{t('crm.settings.loading')}</Text>
            </div>
          ) : (
            renderTabContent()
          )}
        </div>

        {/* Юридические страницы: ссылки внизу настроек, доступны в любом табе. */}
        <div
          className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-4"
          style={{ borderColor: 'var(--ant-color-border)' }}
          data-testid="settings-legal-links"
        >
          <Link
            to="/terms"
            className="text-sm transition-colors hover:text-amber-600"
            style={{ color: 'var(--ant-color-text-tertiary)' }}
          >
            {t('crm.legal.link.terms')}
          </Link>
          <Link
            to="/privacy"
            className="text-sm transition-colors hover:text-amber-600"
            style={{ color: 'var(--ant-color-text-tertiary)' }}
          >
            {t('crm.legal.link.privacy')}
          </Link>
          <Link
            to="/support"
            className="text-sm transition-colors hover:text-amber-600"
            style={{ color: 'var(--ant-color-text-tertiary)' }}
          >
            {t('crm.legal.link.support')}
          </Link>
        </div>
      </Card>

      <Modal
        title={t('crm.settings.confirm_title')}
        open={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        footer={[
          <Button key="back" onClick={() => setIsConfirmOpen(false)}>
            {t('crm.common.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={updateSettingsMutation.isPending}
            onClick={() => void handleConfirmProMode()}
          >
            {t('crm.settings.confirm_submit')}
          </Button>,
        ]}
      >
        <p data-testid="pro-enable-confirm">{t('crm.settings.confirm_body')}</p>
      </Modal>
    </main>
  );
}
